import React, { useState, useEffect, useReducer, useContext } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableContainer from "@material-ui/core/TableContainer";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Tooltip from "@material-ui/core/Tooltip";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import CardActions from "@material-ui/core/CardActions";
import Typography from "@material-ui/core/Typography";
import Alert from "@material-ui/lab/Alert";
import { 
  WhatsApp as WhatsAppIcon,
  Telegram as TelegramIcon,
  Instagram as InstagramIcon,
  Email as EmailIcon,
  Facebook as FacebookIcon
} from "@material-ui/icons";
import StandardTabs from "../../components/StandardTabs";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import SubscriptionModal from "../../components/SubscriptionModal";
import PlanSelectorModal from "../../components/PlanSelectorModal";
import AiUsageLogs from "../AiUsageLogs";
import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import useSettings from "../../hooks/useSettings";
import { toast } from "react-toastify";

import moment from "moment";

const reducer = (state, action) => {
  if (action.type === "LOAD_INVOICES") {
    const invoices = action.payload.invoices || action.payload;
    const newInvoices = [];

    invoices.forEach((invoice) => {
      const invoiceIndex = state.findIndex((i) => i.id === invoice.id);
      if (invoiceIndex !== -1) {
        state[invoiceIndex] = invoice;
      } else {
        newInvoices.push(invoice);
      }
    });

    return [...state, ...newInvoices];
  }

  if (action.type === "UPDATE_INVOICES") {
    const invoice = action.payload;
    const invoiceIndex = state.findIndex((i) => i.id === invoice.id);

    if (invoiceIndex !== -1) {
      state[invoiceIndex] = invoice;
      return [...state];
    } else {
      return [invoice, ...state];
    }
  }

  if (action.type === "DELETE_INVOICE") {
    const invoiceId = action.payload;

    const invoiceIndex = state.findIndex((i) => i.id === invoiceId);
    if (invoiceIndex !== -1) {
      state.splice(invoiceIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  tableHeader: {
    fontWeight: 'bold',
    backgroundColor: "#1e1e1e",
    color: "#fff",
    borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
    position: "sticky",
    top: 0,
    zIndex: 1,
  },
  scrollArea: {
    flex: 1,
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  neonTabSaas: {
    fontWeight: "bold",
    letterSpacing: "1px",
    textTransform: "uppercase",
    transition: "all 0.3s ease",
    color: "#80ffff !important",
    opacity: 0.8,
    "&.Mui-selected": {
      color: "#00ffff !important",
      opacity: 1,
      textShadow: "0 0 3px rgba(0, 255, 255, 0.8), 0 0 8px rgba(0, 255, 255, 0.6) !important",
    },
    "&:hover": {
      color: "#00ffff !important",
      opacity: 1,
      textShadow: "0 0 4px rgba(0, 255, 255, 0.6) !important",
    }
  },
  neonTabAi: {
    fontWeight: "bold",
    letterSpacing: "1px",
    textTransform: "uppercase",
    transition: "all 0.3s ease",
    color: "#80ffaa !important",
    opacity: 0.8,
    "&.Mui-selected": {
      color: "#00ff44 !important",
      opacity: 1,
      textShadow: "0 0 3px rgba(0, 255, 68, 0.8), 0 0 8px rgba(0, 255, 68, 0.6) !important",
    },
    "&:hover": {
      color: "#00ff44 !important",
      opacity: 1,
      textShadow: "0 0 4px rgba(0, 255, 68, 0.6) !important",
    }
  },
  neonTabLogs: {
    fontWeight: "bold",
    letterSpacing: "1px",
    textTransform: "uppercase",
    transition: "all 0.3s ease",
    color: "#cc99ff !important",
    opacity: 0.8,
    "&.Mui-selected": {
      color: "#bb66ff !important",
      opacity: 1,
      textShadow: "0 0 2px rgba(187, 102, 255, 0.5), 0 0 5px rgba(187, 102, 255, 0.3) !important",
    },
    "&:hover": {
      color: "#bb66ff !important",
      opacity: 1,
      textShadow: "0 0 3px rgba(187, 102, 255, 0.4) !important",
    }
  },
}));

const Invoices = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [searchParam,] = useState("");
  const [invoices, dispatch] = useReducer(reducer, []);
  const [storagePlans, setStoragePlans] = React.useState([]);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [planModalOpen, setPlanModalOpen] = useState(false);
  const [isCompanyExpired, setIsCompanyExpired] = useState(false);
  const [currency, setCurrency] = useState("");
  const { getAll: getAllSettings } = useSettings();
  const [tabValue, setTabValue] = useState(0);
  const [aiPackages, setAiPackages] = useState([]);

  // Verificar se a empresa está vencida
  useEffect(() => {
    if (user && user.company) {
      const hoje = moment();
      const vencimento = moment(user.company.dueDate);
      const isExpired = hoje.isAfter(vencimento);
      setIsCompanyExpired(isExpired);
    }
  }, [user]);

  const handleOpenContactModal = (invoices) => {
    setStoragePlans(invoices);
    setSelectedContactId(null);
    setContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(false);
  };

  const handleOpenPlanModal = () => {
    setPlanModalOpen(true);
  };

  const handleClosePlanModal = () => {
    setPlanModalOpen(false);
  };

  const handleBuyAiPackage = async (packageId) => {
    try {
      const { data } = await api.post("/openpay/ai-package", { packageId });
      if (data && data.link) {
        window.open(data.link, "_blank");
      }
    } catch (err) {
      toastError(err);
    }
  };

  const getAiPackageName = (pkg) => {
    if (!pkg) return "";
    const nameLower = (pkg.name || "").toLowerCase();
    if (nameLower.includes("básico") || nameLower.includes("basico") || nameLower.includes("basic")) {
      return i18n.t("finance.aiRecharge.packages.basic");
    }
    if (nameLower.includes("starter")) {
      return i18n.t("finance.aiRecharge.packages.starter");
    }
    if (nameLower.includes("empresarial") || nameLower.includes("enterprise") || nameLower.includes("business")) {
      return i18n.t("finance.aiRecharge.packages.enterprise");
    }
    if (nameLower.includes("corporativo") || nameLower.includes("corporate")) {
      return i18n.t("finance.aiRecharge.packages.corporate");
    }
    return pkg.name;
  };

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        if (user && user.companyId) {
          const settings = await getAllSettings(user.companyId);
          console.log("Settings fetched for currency:", settings);
          const systemCurrency = settings.find((s) => s.key === "system_currency");
          if (systemCurrency && systemCurrency.value) {
            setCurrency(systemCurrency.value);
          } else {
            console.log("system_currency not found or empty, using default $");
            setCurrency("$");
          }
        }
      } catch (error) {
        console.error("Error fetching system currency:", error);
        setCurrency("$");
      }
    };
    fetchSettings();
  }, [user]);

  useEffect(() => {
    const fetchAiPackages = async () => {
      try {
        const { data } = await api.get("/ai-packages");
        setAiPackages(data.filter((pkg) => pkg.status));
      } catch (err) {
        toastError(err);
      }
    };
    if (tabValue === 1) {
      fetchAiPackages();
    }
  }, [tabValue]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchInvoices = async () => {
        try {
          console.log("Buscando faturas...", { searchParam, pageNumber });
          const { data } = await api.get("/invoices/all", {
            params: { searchParam, pageNumber },
          });

          console.log("Dados recebidos:", data);
          dispatch({ type: "LOAD_INVOICES", payload: data });
          console.log("Dispatch realizado com payload:", data);
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          console.error("Erro ao buscar faturas:", err);
          toastError(err);
          setLoading(false);
        }
      };
      fetchInvoices();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const rowStyle = (record) => {
    const hoje = moment(moment()).format("DD/MM/yyyy");
    const vencimento = moment(record.dueDate).format("DD/MM/yyyy");
    var diff = moment(vencimento, "DD/MM/yyyy").diff(moment(hoje, "DD/MM/yyyy"));
    var dias = moment.duration(diff).asDays();
    if (dias < 0 && record.status !== "paid") {
      return { backgroundColor: "#ffbcbc9c" };
    }
  };

  const rowStatus = (record) => {
    const hoje = moment(moment()).format("DD/MM/yyyy");
    const vencimento = moment(record.dueDate).format("DD/MM/yyyy");
    var diff = moment(vencimento, "DD/MM/yyyy").diff(moment(hoje, "DD/MM/yyyy"));
    var dias = moment.duration(diff).asDays();
    const status = record.status;
    if (status === "paid") {
      return i18n.t("finance.status.paid");
    }
    if (dias < 0) {
      return i18n.t("finance.status.overdue");
    } else {
      return i18n.t("finance.status.open");
    }
  }

  const renderUseWhatsapp = (row) => { return row.status === false ? i18n.t("finance.options.no") : i18n.t("finance.options.yes") };
  const renderUseFacebook = (row) => { return row.status === false ? i18n.t("finance.options.no") : i18n.t("finance.options.yes") };
  const renderUseInstagram = (row) => { return row.status === false ? i18n.t("finance.options.no") : i18n.t("finance.options.yes") };
  const renderUseCampaigns = (row) => { return row.status === false ? i18n.t("finance.options.no") : i18n.t("finance.options.yes") };
  const renderUseSchedules = (row) => { return row.status === false ? i18n.t("finance.options.no") : i18n.t("finance.options.yes") };
  const renderUseInternalChat = (row) => { return row.status === false ? i18n.t("finance.options.no") : i18n.t("finance.options.yes") };
  const renderUseExternalApi = (row) => { return row.status === false ? i18n.t("finance.options.no") : i18n.t("finance.options.yes") };

  return (
    <MainContainer>
      <SubscriptionModal
        open={contactModalOpen}
        onClose={handleCloseContactModal}
        aria-labelledby="form-dialog-title"
        Invoice={storagePlans}
        contactId={selectedContactId}

      ></SubscriptionModal>
      <PlanSelectorModal
        open={planModalOpen}
        onClose={handleClosePlanModal}
      />
      <MainHeader>
        <Title>{i18n.t("finance.title")}</Title>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined" onScroll={handleScroll}>
        <div style={{ padding: "14px 16px 8px 16px" }}>
          <StandardTabs
            value={tabValue}
            onChange={(e, v) => setTabValue(v)}
            items={[
              { value: 0, label: i18n.t("finance.tabs.saas") },
              { value: 1, label: i18n.t("finance.tabs.rechargeAi") },
              { value: 2, label: i18n.t("finance.tabs.consumeAi") },
            ]}
          />
        </div>

        {tabValue === 0 && (
          <Box p={2}>
            {isCompanyExpired && (
          <div style={{
            backgroundColor: '#ffebee',
            color: '#c62828',
            padding: '10px',
            borderRadius: '4px',
            margin: '0 auto',
            border: '1px solid #ef9a9a'
          }}>
            <strong>{i18n.t("finance.warnings.attention")}</strong> {user?.company?.resellerId > 0 ? "Su cuenta ha vencido. Por favor, comuníquese con su proveedor para renovar su suscripción." : i18n.t("finance.warnings.expired")}
          </div>
        )}
        <TableContainer className={classes.scrollArea}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {/* <TableCell className={classes.tableHeader} align="center">Id</TableCell> */}
              <TableCell className={classes.tableHeader} align="center">{i18n.t("finance.table.details")}</TableCell>

              <TableCell className={classes.tableHeader} align="center">{i18n.t("finance.table.users")}</TableCell>
              <TableCell className={classes.tableHeader} align="center">{i18n.t("finance.table.connections")}</TableCell>
              <TableCell className={classes.tableHeader} align="center">{i18n.t("finance.table.queues")}</TableCell>
              {/* <TableCell className={classes.tableHeader} align="center">Whatsapp</TableCell>
              <TableCell className={classes.tableHeader} align="center">Facebook</TableCell>
              <TableCell className={classes.tableHeader} align="center">Instagram</TableCell> */}
              {/* <TableCell className={classes.tableHeader} align="center">Campanhas</TableCell>
              <TableCell className={classes.tableHeader} align="center">Agendamentos</TableCell>
              <TableCell className={classes.tableHeader} align="center">Chat Interno</TableCell>
              <TableCell className={classes.tableHeader} align="center">Rest PI</TableCell> */}

              <TableCell className={classes.tableHeader} align="center">{i18n.t("finance.table.value")}</TableCell>
              <TableCell className={classes.tableHeader} align="center">{i18n.t("finance.table.dueDate")}</TableCell>
              <TableCell className={classes.tableHeader} align="center">{i18n.t("finance.table.status")}</TableCell>
              <TableCell className={classes.tableHeader} align="center">{i18n.t("finance.table.action")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {invoices.map((invoices) => (
                <TableRow style={rowStyle(invoices)} key={invoices.id}>
                  {/* <TableCell align="center">{invoices.id}</TableCell> */}
                  <TableCell align="center">{invoices.detail}</TableCell>

                  <TableCell align="center">{invoices.users}</TableCell>
                  <TableCell align="center">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {invoices.connections}
                      <span style={{ marginLeft: 8, display: 'flex', gap: '4px' }}>
                        <Tooltip title="Api Oficial" arrow>
                          <WhatsAppIcon style={{ color: '#25D366', fontSize: '1.2rem', cursor: 'help' }} />
                        </Tooltip>
                        <Tooltip title="Baileys" arrow>
                          <WhatsAppIcon style={{ color: '#ffffff', fontSize: '1.2rem', cursor: 'help' }} />
                        </Tooltip>
                        <Tooltip title="Telegram" arrow>
                          <TelegramIcon style={{ color: '#0088cc', fontSize: '1.2rem', cursor: 'help' }} />
                        </Tooltip>
                        <Tooltip title="Instagram" arrow>
                          <InstagramIcon style={{ color: '#E1306C', fontSize: '1.2rem', cursor: 'help' }} />
                        </Tooltip>
                        <Tooltip title="Messenger" arrow>
                          <FacebookIcon style={{ color: '#1877F2', fontSize: '1.2rem', cursor: 'help' }} />
                        </Tooltip>
                        <Tooltip title="Email" arrow>
                          <EmailIcon style={{ color: '#ffffff', fontSize: '1.2rem', cursor: 'help' }} />
                        </Tooltip>
                      </span>
                    </div>
                  </TableCell>
                  <TableCell align="center">{invoices.queues}</TableCell>
                  {/* <TableCell align="center">{renderUseWhatsapp(invoices.useWhatsapp)}</TableCell>
                  <TableCell align="center">{renderUseFacebook(invoices.useFacebook)}</TableCell>
                  <TableCell align="center">{renderUseInstagram(invoices.useInstagram)}</TableCell> */}
                  {/* <TableCell align="center">{renderUseCampaigns(invoices.useCampaigns)}</TableCell>
                  <TableCell align="center">{renderUseSchedules(invoices.useSchedules)}</TableCell>
                  <TableCell align="center">{renderUseInternalChat(invoices.useInternalChat)}</TableCell>
                  <TableCell align="center">{renderUseExternalApi(invoices.useExternalApi)}</TableCell> */}

                  <TableCell style={{ fontWeight: 'bold' }} align="center">{currency} {invoices.value.toFixed(2)}</TableCell>
                  <TableCell align="center">{moment(invoices.dueDate).format("DD/MM/YYYY")}</TableCell>
                  <TableCell style={{ fontWeight: 'bold' }} align="center">{rowStatus(invoices)}</TableCell>
                  <TableCell align="center">
                    {rowStatus(invoices) !== i18n.t("finance.status.paid") ?
                      (user?.company?.resellerId > 0 ? (
                        user?.company?.reseller?.resellerPaymentLink ? (
                          <Button
                            size="small"
                            variant="outlined"
                            color="secondary"
                            onClick={() => window.open(user.company.reseller.resellerPaymentLink, '_blank')}
                          >
                            {i18n.t("finance.buttons.pay")}
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            variant="outlined"
                            color="secondary"
                            disabled
                          >
                            {i18n.t("finance.planSelector.contactProvider")}
                          </Button>
                        )
                      ) : (
                          <Button
                            size="small"
                            variant="outlined"
                            color="secondary"
                            onClick={handleOpenPlanModal}
                          >
                            {i18n.t("finance.planSelector.hirePlan")}
                          </Button>
                      )) :
                      <Button
                        size="small"
                        variant="outlined"
                      // color="secondary"
                      >
                        {i18n.t("finance.buttons.paid")}
                      </Button>}

                  </TableCell>
                </TableRow>
              ))}
              {invoices.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <p style={{ marginBottom: "15px" }}>{i18n.t("finance.noInvoicesRenew")}</p>
                    <Button
                      size="large"
                      variant="contained"
                      color="primary"
                      onClick={handleOpenPlanModal}
                    >
                      {i18n.t("finance.planSelector.hirePlan")}
                    </Button>
                  </TableCell>
                </TableRow>
              )}
              {loading && <TableRowSkeleton columns={4} />}
            </>
          </TableBody>
        </Table>
        </TableContainer>
          </Box>
        )}

        {tabValue === 1 && (
          <Box p={3}>
            <div style={{ marginBottom: 20, textAlign: 'center' }}>
              <Typography variant="h6">{i18n.t("finance.aiRecharge.currentBalance")} 💰 {parseFloat(user?.company?.aiCredits || 0).toFixed(6)} USD</Typography>
              <Typography variant="body2" color="textSecondary">
                {i18n.t("finance.aiRecharge.description")}
              </Typography>
            </div>
            
            <Box mb={3}>
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>{i18n.t("finance.aiRecharge.howItWorksTitle")}</strong><br />
                  {i18n.t("finance.aiRecharge.howItWorksIntro")}<br /><br />
                  • <strong>{i18n.t("finance.aiRecharge.msgText")}</strong> {i18n.t("finance.aiRecharge.msgVal")}<br />
                  • <strong>{i18n.t("finance.aiRecharge.audioText")}</strong> {i18n.t("finance.aiRecharge.audioVal")}<br />
                  • <strong>{i18n.t("finance.aiRecharge.imageText")}</strong> {i18n.t("finance.aiRecharge.imageVal")}<br /><br />
                  <em>{i18n.t("finance.aiRecharge.note")}</em>
                </Typography>
              </Alert>
            </Box>

            <Grid container spacing={3} justifyContent="center">
              {aiPackages.length === 0 ? (
                <Typography>{i18n.t("finance.aiRecharge.noPackages")}</Typography>
              ) : (
                aiPackages.map((pkg) => (
                  <Grid item xs={12} sm={6} md={4} key={pkg.id}>
                    <Card style={{ height: '100%', display: 'flex', flexDirection: 'column', textAlign: 'center' }}>
                      <CardContent style={{ flexGrow: 1 }}>
                        <Typography variant="h5" component="h2" gutterBottom>
                          {getAiPackageName(pkg)}
                        </Typography>
                        <Typography variant="h4" color="primary">
                          ${pkg.price} USD
                        </Typography>
                        <Typography variant="subtitle1" style={{ marginTop: 10 }}>
                          {i18n.t("finance.aiRecharge.givesYou")} <b>{pkg.credits} USD</b> {i18n.t("finance.aiRecharge.ofBalance")}
                        </Typography>
                        {(pkg.credits - pkg.price) > 0 && (
                          <Typography variant="body2" style={{ color: "#4caf50", marginTop: 8, fontWeight: "bold" }}>
                            {i18n.t("finance.aiRecharge.bonusText", {
                              amount: Number(pkg.credits - pkg.price).toFixed(2),
                              images: Math.floor((pkg.credits - pkg.price) / 0.04)
                            })}
                          </Typography>
                        )}
                      </CardContent>
                      <CardActions style={{ justifyContent: 'center', paddingBottom: 20 }}>
                        <Button 
                          variant="contained" 
                          color="primary" 
                          onClick={() => handleBuyAiPackage(pkg.id)}
                        >
                          {i18n.t("finance.aiRecharge.buyBtn")}
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))
              )}
            </Grid>
          </Box>
        )}

        {tabValue === 2 && (
          <Box p={3} display="flex" flexDirection="column" flex={1} overflow="hidden">
            <AiUsageLogs />
          </Box>
        )}
      </Paper>
    </MainContainer>
  );
};

export default Invoices;
